import { Event, EventHint } from '@sentry/react';

/**
 * Third-party domain + error pattern combinations to ignore
 * Only filters specific known noise from third-party services
 */
interface IgnoredThirdPartyError {
	domain: string;
	errorPatterns: RegExp[];
	reason: string;
}

export const ignoredThirdPartyErrors: IgnoredThirdPartyError[] = [
	{
		domain: 'wchat.freshchat.com',
		errorPatterns: [/JSON Parse error.*Unexpected identifier/, /JSON\.parse/i],
		reason: 'Freshchat widget internal parsing errors - not actionable',
	},
	// Add more third-party error configurations here
];

/**
 * Helper to safely extract hostname from URL
 */
const getHostnameFromUrl = (url: string | undefined): string | null => {
	if (!url) return null;
	try {
		// Handle absolute URLs
		if (url.startsWith('http://') || url.startsWith('https://')) {
			return new URL(url).hostname;
		}
		// Handle protocol-relative URLs
		if (url.startsWith('//')) {
			return new URL(`https:${url}`).hostname;
		}
		// Not a URL, might be just a domain or path
		return null;
	} catch {
		return null;
	}
};

/**
 * Custom beforeSend hook to filter out specific third-party errors
 *
 * Only filters errors that match BOTH:
 * 1. Origin from a specific third-party domain
 * 2. Match a known error pattern for that domain
 *
 * This approach is safer than blanket domain blocking because:
 * - Won't hide potentially critical third-party integration errors
 * - Only filters known noise patterns
 * - Easier to debug integration issues
 */
export const sentryBeforeSend = (event: Event, hint?: EventHint): Event | null => {
	// Check if this error should be filtered
	const shouldFilter = event.exception?.values?.some((exception) => {
		const errorMessage = exception.value || '';

		// Check if any frame comes from an ignored third-party domain
		return exception.stacktrace?.frames?.some((frame) => {
			const frameHostname = getHostnameFromUrl(frame.abs_path);
			if (!frameHostname) return false;

			// Find matching third-party error config
			const matchingConfig = ignoredThirdPartyErrors.find(
				(config) => frameHostname === config.domain || frameHostname.endsWith(`.${config.domain}`),
			);

			if (!matchingConfig) return false;

			// Check if error message matches any of the patterns for this domain
			return matchingConfig.errorPatterns.some((pattern) => pattern.test(errorMessage));
		});
	});

	if (shouldFilter) {
		return null;
	}

	return event;
};
